import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  effect,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {ChallengeBuilderProject, ChallengeGoal} from 'src/app/core/model/project';
import {
  ChallengeGoalAction,
  ChallengeRefineGoalAction,
  SetBackLoadingAction,
  SetNextLoadingAction,
  UpdateChallengeNextValidAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {MessageService} from 'src/app/services/message.service';
import {OphButtonModule} from '../../../shared/design/oph-button/oph-button.module';
import {CbCheckboxOptionComponent} from '../shared/cb-checkbox-option.component';

@Component({
  selector: 'cb-goal',
  standalone: true,
  imports: [CbCheckboxOptionComponent, OphButtonModule],
  templateUrl: './cb-goal.component.html',
  styleUrl: './cb-goal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbGoalComponent implements OnInit {
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);
  private destroyed = false;
  private nextClickSubscription?: Subscription;
  private backClickSubscription?: Subscription;

  nextClick$ = input.required<Observable<void>>();
  backClick$ = input.required<Observable<void>>();
  cbProject = input<ChallengeBuilderProject | undefined>();

  onNext = output<void>();
  onBack = output<void>();

  // Internal form state
  userGoal = signal(''); // The user's original input
  goal = signal(''); // The selected refined goal
  goalSuggestions = signal<string[]>([]);

  // UI state
  goalText = signal('');
  showOptions = signal(false);
  onGoLoading = signal(false);
  shouldAnimateOptions = signal(false);
  selectedOption = signal<string | null>(null);
  originalSelectedGoal = '';

  // Computed signals for template bindings
  optionStates = computed(() => {
    const selected = this.selectedOption();
    return this.goalSuggestions().map(option => ({
      text: option,
      isChecked: selected === option,
      isDisabled: false,
      isInput: true,
    }));
  });

  goalOptions = computed(() => this.goalSuggestions());
  useAi = computed(() => this.cbProject()?.challengeBuilder?.useAi !== false);

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.nextClickSubscription?.unsubscribe();
      this.backClickSubscription?.unsubscribe();
    });

    effect(
      () => {
        const useAi = this.useAi();
        if (useAi) {
          this.store$.dispatch(new UpdateChallengeNextValidAction({valid: !!this.selectedOption()?.trim()}));
        } else {
          this.store$.dispatch(new UpdateChallengeNextValidAction({valid: !!this.goalText()?.trim()}));
        }
      },
      {allowSignalWrites: true}
    );
  }

  ngOnInit(): void {
    // Subscribe to next/back button clicks from parent
    this.nextClickSubscription = this.nextClick$().subscribe(() => {
      this.onNextClick();
    });
    this.backClickSubscription = this.backClick$().subscribe(() => {
      this.onBackClick();
    });

    // Initialize form state from project if returning to this step
    const challenge = this.cbProject()?.challengeBuilder;
    if (!this.useAi()) {
      if (challenge?.initialGoal) {
        this.goalText.set(challenge.initialGoal);
        this.userGoal.set(challenge.initialGoal);
        this.store$.dispatch(new UpdateChallengeNextValidAction({valid: true}));
      }
    } else if (challenge?.goals?.length) {
      this.goalText.set(challenge.initialGoal || '');
      this.userGoal.set(challenge.initialGoal || '');
      this.goalSuggestions.set(challenge.goals.map(g => g.name));
      this.showOptions.set(true);
      const selectedGoal = challenge.goals.find(g => g.selected);
      this.originalSelectedGoal = selectedGoal?.name || '';
      if (selectedGoal) {
        this.selectedOption.set(selectedGoal.name);
        this.goal.set(selectedGoal.name);
        this.store$.dispatch(new UpdateChallengeNextValidAction({valid: true}));
      }
    }
  }

  onGoalInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.goalText.set(target.value);

    if (!this.useAi()) {
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: !!target.value.trim()}));
    }
  }

  onEnterKey(event: Event) {
    if (this.useAi()) {
      // Only trigger if there's text and not loading
      if (this.goalText() && !this.onGoLoading()) {
        event.preventDefault(); // Prevent newline in textarea
        this.onGoClick();
      }
    } else {
      if (this.goalText()?.trim()) {
        event.preventDefault();
        this.onNextClick();
      }
    }
  }

  onGoClick() {
    this.onGoLoading.set(true);
    // Enable animations for new results
    this.shouldAnimateOptions.set(true);

    // Save the user's input
    this.userGoal.set(this.goalText());

    this.store$.dispatch(
      new ChallengeRefineGoalAction({
        projectId: this.cbProject()?._id || '',
        initialGoal: this.goalText(),
        onSuccess: (cbProject: ChallengeBuilderProject) => this.handleGoalRefinementSuccess(cbProject),
        onFailure: (error: Error) => this.handleGoalRefinementFailure(error),
      })
    );
  }

  private handleGoalRefinementSuccess(cbProject: ChallengeBuilderProject): void {
    if (this.destroyed) return;
    this.onGoLoading.set(false);
    if (cbProject.challengeBuilder.goals.length === 0) {
      this.messageService.add('Please enter a valid goal.', 'error');
      return;
    }

    // Clear previous selection and update suggestions
    this.selectedOption.set(null);
    this.goal.set('');
    this.store$.dispatch(new UpdateChallengeNextValidAction({valid: false}));
    this.goalSuggestions.set(cbProject.challengeBuilder.goals.map(g => g.name));
    this.showOptions.set(true);
  }

  private handleGoalRefinementFailure(error: Error): void {
    if (this.destroyed) return;
    this.onGoLoading.set(false);
    this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
    console.error('Failed to refine challenge goal:', error);
  }

  onOptionToggle(index: number, checked: boolean) {
    const option = this.goalSuggestions()[index];
    if (checked) {
      // Set this option as selected
      this.selectedOption.set(option);
      this.goal.set(option);
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: !!option.trim()}));
    } else {
      // Clear selection if unchecking
      this.selectedOption.set(null);
      this.goal.set('');
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: false}));
    }
  }

  onOptionTextChange(index: number, newText: string) {
    const suggestions = [...this.goalSuggestions()];
    const oldText = suggestions[index];
    suggestions[index] = newText;
    this.goalSuggestions.set(suggestions);

    // Update selected option if this was the selected one
    if (this.selectedOption() === oldText) {
      this.selectedOption.set(newText);
      this.goal.set(newText);
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: !!newText.trim()}));
    }
  }

  // Public method to get form data
  getFormData() {
    return {
      userGoal: this.userGoal(),
      goal: this.goal(),
      goalSuggestions: this.goalSuggestions(),
    };
  }

  private onBackClick(): void {
    let goals: ChallengeGoal[];

    if (this.useAi()) {
      goals = this.optionStates().map(option => ({
        name: option.text,
        selected: option.isChecked,
      }));
    } else {
      const goalText = this.goalText().trim();
      this.userGoal.set(goalText);
      goals = [{name: goalText, selected: true}];
    }

    this.store$.dispatch(
      new ChallengeGoalAction({
        projectId: this.cbProject()?._id || '',
        goals,
        goalAltered: false,
        onSuccess: () => {
          if (this.destroyed) return;
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
          this.onBack.emit();
        },
        onFailure: (error: Error) => {
          if (this.destroyed) return;
          this.messageService.add('Failed to save goal', 'error');
          console.error('Failed to save goal:', error);
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
      })
    );
  }

  private onNextClick(): void {
    let goals: ChallengeGoal[];
    let goalAltered: boolean;

    if (this.useAi()) {
      goals = this.optionStates().map(option => ({
        name: option.text,
        selected: option.isChecked,
      }));
      const currentSelectedGoal = goals.find(g => g.selected)?.name || '';
      goalAltered = this.originalSelectedGoal !== currentSelectedGoal;
    } else {
      const goalText = this.goalText().trim();
      this.userGoal.set(goalText);
      goals = [{name: goalText, selected: true}];
      goalAltered = this.originalSelectedGoal !== goalText;
    }

    this.store$.dispatch(
      new ChallengeGoalAction({
        projectId: this.cbProject()?._id || '',
        goals,
        goalAltered,
        onSuccess: (cbProject: ChallengeBuilderProject) => this.handleGoalSuccess(cbProject),
        onFailure: (error: Error) => this.handleGoalRefinementFailure(error),
      })
    );
  }

  private handleGoalSuccess(cbProject: ChallengeBuilderProject): void {
    if (this.destroyed) return;
    this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
    this.onNext.emit();
  }
}
